import type { SelectChangeEvent } from '@mui/material'
import type { TFunction } from 'i18next'
import type { FC } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import type { MarketProgram } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useCountryBidExportMarketProgramMap } from '@/features/bidding/hooks/useCountryBidExportMarketProgramMap'
import type { MarketProgramBiddingName } from '@/features/bidding/types/bid'

type Props = { value: MarketProgramBiddingName | ''; onChange: (event: SelectChangeEvent) => void }

const BidExportMarketProgramSelector: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const { selectedCountry } = useBiddingContext()

  const countryBidExportMarketProgramMap = useCountryBidExportMarketProgramMap()
  const sortedMarketPrograms = useMemo(
    () => countryBidExportMarketProgramMap[selectedCountry].toSorted((a, b) => a.localeCompare(b)),
    [],
  )

  return (
    <CustomSelectField
      showAllOption
      data-testid="market-program-selector"
      id="marketProgram"
      label={t('bidding.overview.header.market_program')}
      options={getMarketProgramOptions(sortedMarketPrograms, t)}
      sx={{ minWidth: FIELD_WIDTH }}
      value={value}
      onChange={onChange}
    />
  )
}

const getMarketProgramOptions = (
  sortedMarketPrograms: MarketProgram[],
  t: TFunction<'translation'>,
): SelectFieldOption<MarketProgramBiddingName>[] =>
  (sortedMarketPrograms ?? []).map((market: MarketProgram) => ({
    value: market as MarketProgramBiddingName,
    id: `marketProgram-filter-item-${market}`,
    label: t(`bidding.market_program.${market}`),
  }))

export default BidExportMarketProgramSelector
